import propTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';

export const PricingPlan = (props) => {
  const {
    onClick,
    cta,
    currency,
    code,
    description,
    features,
    image,
    name,
    popular,
    price,
    sx,
    ...other
  } = props;

  const displayCode = code
    ? (String(code).startsWith('#') ? code : `#${code}`)
    : name;

  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        backgroundColor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2.5,
        boxSizing: 'border-box',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
        width: '100%',
        '@media (hover: hover)': {
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: (theme) => theme.palette.mode === 'dark'
              ? `0 12px 32px rgba(0,0,0,0.45)`
              : `0 12px 32px rgba(15,23,42,0.1)`,
            transform: 'translateY(-3px)',
            '& .product-image': {
              transform: 'scale(1.04)'
            }
          }
        },
        ...sx
      }}
      {...other}
    >
      <Box
        className="product-image"
        sx={{
          aspectRatio: '3 / 4',
          backgroundColor: (theme) =>
            theme.palette.mode === 'dark' ? 'neutral.900' : 'neutral.100',
          backgroundImage: image ? `url(${image})` : 'none',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          flexShrink: 0,
          overflow: 'hidden',
          position: 'relative',
          transition: 'transform 0.35s ease',
          width: '100%'
        }}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          gap: 0.25,
          minWidth: 0,
          p: { xs: 1.25, sm: 1.5 }
        }}
      >
        <Typography
          sx={{
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            display: '-webkit-box',
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            fontWeight: 600,
            lineHeight: 1.35,
            minHeight: '2.5em',
            overflow: 'hidden'
          }}
        >
          {name}
        </Typography>
        <Typography
          color="primary.main"
          noWrap
          sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' }, fontWeight: 700, lineHeight: 1.3, mt: 0.25 }}
        >
          {`${currency} ${price}`}
        </Typography>
        <Typography
          color="text.secondary"
          noWrap
          sx={{ fontSize: '0.75rem', mb: 1 }}
          variant="caption"
        >
          {displayCode}
        </Typography>
        <Button
          fullWidth
          onClick={(e) => {
            e.stopPropagation();
            onClick?.();
          }}
          sx={{
            borderRadius: 2,
            fontSize: '0.8125rem',
            fontWeight: 600,
            mt: 'auto',
            py: 0.85,
            textTransform: 'none',
            ...(popular && {
              boxShadow: (theme) => `0 4px 12px ${theme.palette.primary.main}35`
            })
          }}
          variant={popular ? 'contained' : 'outlined'}
        >
          {cta}
        </Button>
      </Box>
    </Card>
  );
};

PricingPlan.propTypes = {
  cta: propTypes.string.isRequired,
  currency: propTypes.string.isRequired,
  code: propTypes.string,
  description: propTypes.string.isRequired,
  features: propTypes.array.isRequired,
  icon: propTypes.any.isRequired,
  name: propTypes.string.isRequired,
  popular: propTypes.bool,
  price: propTypes.string.isRequired,
  sx: propTypes.object
};
